import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ChevronRight, MapPin, RefreshCw } from 'lucide-react';

import BottomNav from '@/components/custom/BottomNav';
import { Button } from '@/components/ui/button';
import { API_BASE as API } from '@/config/api';
import { RECORRIDOS_BY_ID, getLandingCardColors } from '@/data/recorridos/config';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { motivoSinServicioHoy } from '@/lib/calendarioServicio';
import { MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL } from '@/lib/paraderoRecorrido';
import { CERCA_PATH, paraderoPath } from '@/lib/paraderoRoutes';
import { getParaderoFisico } from '@/lib/paraderosFisicos';
import { setStoredRecorridoId } from '@/lib/recorridoStorage';

/** Refresco del tablero (ms). El GPS de los buses cambia cada ~10 s. */
const REFRESH_MS = 15000;

const FECHA_SANTIAGO = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' });
/** «lunes 19 de octubre» para una fecha YYYY-MM-DD (se interpreta en UTC: no se corre de día). */
const FECHA_LARGA = new Intl.DateTimeFormat('es-CL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

const mayuscula = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fechaLarga = (iso) => FECHA_LARGA.format(new Date(`${iso}T00:00:00Z`)).replace(',', '');
const HORA_SANTIAGO = new Intl.DateTimeFormat('es-CL', {
  timeZone: 'America/Santiago',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** «Mañana» / «Lunes 19 de octubre» para una pasada que no es hoy (fecha YYYY-MM-DD de Chile). */
function diaRelativo(fecha) {
  if (!fecha) return '';
  const hoy = new Date(`${FECHA_SANTIAGO.format(new Date())}T00:00:00Z`);
  const dia = new Date(`${fecha}T00:00:00Z`);
  const dias = Math.round((dia - hoy) / 86400000);
  if (dias === 1) return 'Mañana';
  return mayuscula(fechaLarga(fecha));
}

function LlegadaFila({ llegada, onOpen }) {
  const recorrido = RECORRIDOS_BY_ID[llegada.trayectoId];
  const color = getLandingCardColors(recorrido).background;
  const enVivo = llegada.estado === 'en_vivo';
  const programado = llegada.estado === 'programado';

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(llegada.trayectoId)}
        className="flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-card p-4 text-left shadow-card transition-all hover:bg-muted/40 active:scale-[0.99]"
        data-testid={`llegada-${llegada.trayectoId}-${llegada.sentido}`}
        aria-label={`${llegada.codigo} hacia ${llegada.destino}: ver recorrido en el mapa`}
      >
        <span
          className="flex h-11 min-w-[3.25rem] shrink-0 items-center justify-center rounded-xl px-2 font-headline text-sm font-extrabold text-white"
          style={{ backgroundColor: color }}
        >
          {llegada.codigo}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground">
            {llegada.sentido === 'ida' ? 'Ida' : 'Regreso'} · hacia
          </span>
          <span className="block font-headline text-[15px] font-bold leading-snug text-foreground">
            {llegada.destino}
          </span>
          {!llegada.tiempoReal && (
            <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
              {MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL}
            </span>
          )}
          {programado && llegada.atrasoMin != null && (
            <span className="mt-0.5 block text-xs font-semibold leading-snug text-foreground">
              Bus atrasado: llegaría cerca de las {llegada.horaEstimada}
            </span>
          )}
          {llegada.aprox && (
            <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
              Ubicación del paradero aproximada
            </span>
          )}
        </span>

        <span className="shrink-0 text-right">
          {enVivo && (
            <>
              <span className="flex items-center justify-end gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden />
                En vivo
              </span>
              <span className="block font-headline text-2xl font-extrabold tabular-nums leading-tight text-foreground">
                {llegada.etaMin <= 1 ? 'Llegando' : `${llegada.etaMin} min`}
              </span>
              {llegada.etaMin > 1 && (
                <span className="block text-xs tabular-nums text-muted-foreground">
                  aprox. {llegada.horaEstimada}
                </span>
              )}
            </>
          )}
          {programado && (
            <>
              <span className="block text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                Programado
              </span>
              <span className="block font-headline text-2xl font-extrabold tabular-nums leading-tight text-foreground">
                {llegada.horaProgramada}
              </span>
              {!llegada.esHoy && (
                <span className="block text-xs font-bold text-foreground">
                  {diaRelativo(llegada.fechaProgramada)}
                </span>
              )}
            </>
          )}
          {!enVivo && !programado && (
            <span className="block text-sm font-semibold text-muted-foreground">Sin servicio</span>
          )}
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    </li>
  );
}

export default function LlegadasView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paraderoId = searchParams.get('paradero');
  /** Nombre desde el catálogo local: el encabezado no espera a la API. */
  const paraderoLocal = getParaderoFisico(paraderoId);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const cargadoRef = useRef(false);

  useDocumentTitle(paraderoLocal ? `Llegadas ${paraderoLocal.nombre}` : 'Llegadas');

  const fetchLlegadas = useCallback(
    async (manual = false) => {
      if (!paraderoId) return;
      if (manual) setRefreshing(true);
      try {
        const res = await axios.get(
          `${API}/paraderos-fisicos/${encodeURIComponent(paraderoId)}/llegadas`
        );
        setData(res.data);
        setError(null);
        cargadoRef.current = true;
      } catch (err) {
        const noExiste = err.response?.status === 404;
        // Un fallo de red en un refresco no borra el tablero ya mostrado.
        if (noExiste || !cargadoRef.current) {
          setData(null);
          setError(noExiste ? 'No encontramos ese paradero.' : 'No se pudieron cargar las llegadas.');
        }
      } finally {
        setRefreshing(false);
      }
    },
    [paraderoId]
  );

  useEffect(() => {
    cargadoRef.current = false;
    setData(null);
    setError(null);
    fetchLlegadas();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchLlegadas();
    }, REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchLlegadas();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchLlegadas]);

  const abrirRecorrido = (recorridoId) => {
    setStoredRecorridoId(recorridoId);
    navigate(paraderoPath({ recorridoId }));
  };

  /** Se recalcula en cada refresco (15 s): el encabezado cambia de día a medianoche. */
  const sinServicio = motivoSinServicioHoy();
  const nombre = data?.paradero?.nombre || paraderoLocal?.nombre || 'Paradero';
  const llegadas = data?.llegadas || [];
  const hayEnVivo = llegadas.some((l) => l.estado === 'en_vivo');

  return (
    <div className="min-h-screen bg-background px-4 pb-32 pt-8" data-testid="llegadas-view">
      <div className="mx-auto max-w-md space-y-6">
        <header className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" aria-label="Ir al inicio" className="shrink-0 rounded-lg">
              <img
                src={`${process.env.PUBLIC_URL || ''}/img/Logo-MuniBus.png`}
                alt=""
                className="h-10 w-auto"
              />
            </Link>
            <Link
              to={CERCA_PATH}
              className="text-xs font-bold uppercase tracking-widest text-primary underline decoration-primary/40 underline-offset-4"
              data-testid="llegadas-cambiar-paradero"
            >
              Cambiar paradero
            </Link>
          </div>
          <div className="pl-1">
            <p className="mb-1 flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              Próximas llegadas
            </p>
            <h1
              className="font-headline text-3xl font-extrabold leading-tight tracking-tight text-foreground"
              data-testid="llegadas-paradero-nombre"
            >
              {nombre}
            </h1>
            <p className="mt-1 text-sm font-semibold text-muted-foreground" data-testid="llegadas-fecha">
              Hoy, {fechaLarga(FECHA_SANTIAGO.format(new Date()))}
              {sinServicio && (
                <span className="font-bold text-foreground"> · Sin servicio hoy ({sinServicio})</span>
              )}
            </p>
          </div>
        </header>

        {!data && !error && (
          <div className="space-y-3" aria-busy="true" aria-label="Cargando llegadas">
            <div className="skeleton h-20 w-full rounded-2xl" />
            <div className="skeleton h-20 w-full rounded-2xl" />
            <div className="skeleton h-20 w-full rounded-2xl" />
          </div>
        )}

        {error && (
          <div
            className="rounded-2xl border border-destructive/35 bg-destructive/10 p-4"
            role="alert"
            data-testid="llegadas-error"
          >
            <p className="flex items-center gap-2 font-semibold text-destructive">
              <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden />
              {error}
            </p>
            <div className="mt-3 flex gap-2">
              <Button type="button" onClick={() => fetchLlegadas(true)} className="touch-target flex-1">
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                Reintentar
              </Button>
              <Button asChild variant="outline" className="touch-target flex-1">
                <Link to={CERCA_PATH}>Elegir paradero</Link>
              </Button>
            </div>
          </div>
        )}

        {data && llegadas.length === 0 && (
          <p className="rounded-2xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
            Ningún recorrido tiene pasadas informadas por este paradero.
          </p>
        )}

        {llegadas.length > 0 && (
          <>
            <ul className="space-y-3" data-testid="llegadas-lista">
              {llegadas.map((l) => (
                <LlegadaFila
                  key={`${l.trayectoId}-${l.sentido}`}
                  llegada={l}
                  onOpen={abrirRecorrido}
                />
              ))}
            </ul>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {hayEnVivo
                ? 'Los minutos «en vivo» se estiman con la posición GPS del bus y pueden variar por tránsito. '
                : ''}
              «Programado» es la hora del itinerario oficial (lunes a viernes, salvo feriados). Actualizado a las{' '}
              {HORA_SANTIAGO.format(new Date(data.actualizado))}.
            </p>
          </>
        )}
      </div>

      <BottomNav onRefresh={() => fetchLlegadas(true)} refreshing={refreshing} />
    </div>
  );
}
